<!-----------------------------------------------------------------------------
  Copyright (c) 2016 - 2021.                                                  -
  Yega Yazilim ve Bilisim Hizmetleri Dis Tic Ltd Sti (Yega Yazilim)           -
  All Rights Reserved.                                                        -
                                                                              -
  The software and data contained herein are confidential and proprietary     -
  information of Yega Yazilim ("Confidential Information").                   -
                                                                              -
  Users of this software may not disclose such Confidential Information and   -
  shall use it only in accordance with the terms of the license agreement     -
  they entered into with Yega Yazilim.                                        -
                                                                              -
  Author:  Ahmet Dogramaci                                                    -
  Date:    2021-09-13                                                         -
  ----------------------------------------------------------------------------->

<template>
  <div>
    <b-container style="margin-top: 70px">
      <b-row>
        <b-col>
          <b-card no-body>
            <b-card-header>
              <h5>Part Search</h5>
            </b-card-header>
            <b-card-body>
              <b-form class="mb-3">
                <b-container fluid>
                  <b-row>
                    <b-col xl="6" lg="6" md="12" sm="12" >
                      <b-form-group label="Part Number(s)" label-class="font-weight-bold">
                        <b-form-textarea v-model="search_form.part_numbers"></b-form-textarea>
                      </b-form-group>
                    </b-col>
                    <b-col xl="6" lg="6" md="12" sm="12" class="d-flex">
                      <b-button variant="primary"  @click="startSearch()" class="align-self-center">
                        Search
                      </b-button>
                      </b-col>
                  </b-row>
                </b-container>
              </b-form>
              <b-alert
                v-if="save_errors.length > 0"
                variant="danger"
                show
                dismissible
                @dismissed="save_errors = []"
              >
                Could not add the following part numbers:
                <strong>{{ save_errors.map(e => e.part_number).join(', ') }}</strong>.
                Please try again or contact support.
              </b-alert>
              <b-table
                  striped
                  hover
                  small
                  stacked="lg"
                  :items="displayItems"
                  :fields="fields"
                  show-empty
                  empty-text="There are no records to show">
                <template v-slot:head(selected)>
                  <b-form-checkbox
                    :checked="allSelected"
                    :indeterminate="someSelected"
                    :disabled="saving"
                    @change="toggleSelectAll"
                  />
                </template>
                <template v-slot:cell(selected)="data">
                  <b-form-checkbox
                    v-if="data.item.status === 'FOUND'"
                    :value="data.item"
                    :disabled="saving"
                    v-model="selected_items"
                  />
                </template>
                <template v-slot:cell(part_number)="data">
                  {{ data.item.part_number }}
                </template>
                <template v-slot:cell(part_description)="data">
                  {{ data.item.part_description }}
                </template>
                <template v-slot:cell(msrp_price)="data">
                  {{ formatCurrency(data.item.msrp_price) }}
                </template>
                <template v-slot:cell(breakeven_price)="data">
                  {{ formatCurrency(data.item.breakeven_price) }}
                </template>
                <template v-slot:head(action)>
                  <div v-if="displayItems.length > 1" class="d-flex align-items-center justify-content-end">
                    <span v-if="saving" class="mr-2 small text-muted text-nowrap">
                      Added {{ save_progress.completed }} of {{ save_progress.total }}
                    </span>
                    <b-dropdown variant="primary" no-caret :disabled="saving" right>
                      <template #button-content>
                        <b-spinner v-if="saving" small />
                        <b-icon-list-check v-else />
                      </template>
                      <b-dropdown-item @click="toggleSelectMode">Select Parts</b-dropdown-item>
                      <b-dropdown-item :disabled="selected_items.length === 0" @click="addSelectedToQuote">
                        Add Selected to Quote ({{ selected_items.length }})
                      </b-dropdown-item>
                    </b-dropdown>
                  </div>
                </template>
                <template v-slot:cell(action)="data">
                  <template v-if="data.item.status === 'FOUND'">
                    <b-button variant="primary" :disabled="saving" @click="addToQuote(data.item)" class="text-nowrap">
                      Add to Quote
                    </b-button>
                  </template>
                  <template v-else>
                    <a href="#" @click.prevent="toggleDetails(data.item)" class="small text-muted">
                      {{ getStatusLabel(data.item.status) }}
                      <span v-if="data.item._showDetails">[-]</span>
                      <span v-else>[+]</span>
                    </a>
                  </template>
                </template>
                <template v-slot:row-details="data">
                  <div class="p-3 bg-light border-top" style="white-space: pre-line">
                    {{ data.item.message }}
                  </div>
                </template>
              </b-table>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </b-container>

    <b-modal
      id="part-number-mismatch-modal"
      title="Part Number Changed"
      no-close-on-backdrop
      hide-header-close
    >
      <template v-if="mismatch">
        <p>
          The part number you searched for
          <strong>{{ mismatch.searched }}</strong>
          was resolved to
          <strong>{{ mismatch.resolved }}</strong>
          due to configuration rules.
        </p>

        <template v-if="mismatch.componentChanges && mismatch.componentChanges.length">
          <p>The following components were changed:</p>
          <b-table-simple small bordered>
            <b-thead>
              <b-tr>
                <b-th>Component</b-th>
                <b-th>Searched</b-th>
                <b-th>Resolved to</b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr v-for="change in mismatch.componentChanges" :key="change.category">
                <b-td>{{ change.category }}</b-td>
                <b-td>{{ change.from ? change.from.join(', ') : '—' }}</b-td>
                <b-td>{{ change.to   ? change.to.join(', ')   : '—' }}</b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </template>

        <p>
          The item has been added to your quote with the resolved part number.
          You can review the configuration to correct the selection, or continue to your quote.
        </p>
      </template>

      <template #modal-footer>
        <b-button variant="primary" @click="goToConfiguration">
          Review Configuration
        </b-button>
        <b-button variant="outline-secondary" @click="addAnyway">
          Add Anyway
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import useCurrencyFormatter from "../../compositions/use-currency-formatter";
import { BIconListCheck } from '@/components/yega/bootstrap/icons';

export default {
  name: "part-search",
  components: { BIconListCheck },
  props:{
    searchMacroName:{
      type: String,
      default:'dynabook_bid_part_number_search'
    },
    partNumberAttributeName:{
      type: String,
      default:'lookedup_part_number'
    },
    productDescriptionAttributeName:{
      type: String,
      default:'quote_product_description'
    },
    isPublic:{
      type: Boolean,
      default: false
    },
    isBidDesk: {
      type: Boolean,
      default: false
    }
  },
  setup(){
    const { formatCurrency } = useCurrencyFormatter();
    return {
      formatCurrency
    };
  },
  data(){
    return {
      search_form:{},
      items:[],
      mismatch: null,
      in_select_mode: false,
      selected_items: [],
      saving: false,
      save_progress: { completed: 0, total: 0 },
      mismatch_queue: [],
      save_errors: []
    };
  },
  computed: {
    canViewBreakeven() {
      const user = this.$store.state.CurrentUserStore.user;
      return user && user.attribute_store && user.attribute_store.can_view_breakeven === 'YES';
    },
    fields() {
      let fields = [];
      if (this.in_select_mode) {
        fields.push({key:'selected',label:''});
      }
      fields.push(
        {key:'part_number',label:'Part Number'},
        {key:'part_description',label:'Description'},
        {key:'msrp_price',label:'MSRP'},
      );
      if(this.isBidDesk || this.canViewBreakeven){
        fields.push({key:'breakeven_price',label:'B/E'});
      }
      fields.push({key:'action',label:''});
      return fields;
    },
    displayItems() {
      // Filter out NOT_FOUND items - they are not displayed
      return this.items.filter(item => item.status !== 'NOT_FOUND');
    },
    foundItems() {
      return this.displayItems.filter(i => i.status === 'FOUND');
    },
    // selected_items holds object references and can only contain FOUND items
    // because checkboxes do not render on non-FOUND rows. No additional filtering needed.
    allSelected() {
      return this.foundItems.length > 0 &&
             this.selected_items.length === this.foundItems.length;
    },
    someSelected() {
      return this.selected_items.length > 0 &&
             this.selected_items.length < this.foundItems.length;
    }
  },
  methods:{
    toggleSelectMode() {
      this.in_select_mode = !this.in_select_mode;
      if (!this.in_select_mode) {
        this.selected_items = [];
      }
    },
    startSearch(){
      this.selected_items  = [];
      this.save_errors     = [];
      this.save_progress   = { completed: 0, total: 0 };
      this.mismatch_queue  = [];
      this.in_select_mode  = false;
      this.$router.push({ name: 'part_search_path', query: this.search_form})
      let search_query = {id:this.searchMacroName,...this.search_form};
      this.$store.dispatch('MacroResultsStore/execute',search_query)
          .then((result)=>{
            this.items = result;
          })
          .catch((error)=>{
            alert(error.response.data.response);
          });
    },
    toggleDetails(item) {
      this.$set(item, '_showDetails', !item._showDetails);
    },
    getStatusLabel(status) {
      const labels = {
        'INVALID_CONFIG': 'Invalid Config',
        'NO_TEMPLATE': 'No Template'
      };
      return labels[status] || status;
    },
    toggleSelectAll(checked) {
      this.selected_items = checked ? [...this.foundItems] : [];
    },
    addToQuote(item){
      if(this.isPublic){
        this.savePublicProduct(item);
      }else{
        this.saveOtherProduct(item);
      }
    },
    _saveProduct(item) {
      return this.isPublic
        ? this.$store.dispatch('PublicQuoteProductsStore/create', {
            cfg_template_version_id: item.cfg_template_version_id,
            cfg_item_ids: item.cfg_item_ids
          })
        : this.$store.dispatch('QuoteProductsStore/create', {
            quote_part_id: this.$route.params.quote_part_id,
            cfg_template_version_id: item.cfg_template_version_id,
            cfg_item_ids: item.cfg_item_ids
          });
    },
    savePublicProduct(item){
      const searchedPartNumber = item.part_number;
      const componentChanges   = item.component_changes || [];
      this._saveProduct(item)
          .then((savedItem) => {
            const resolvedPartNumber = savedItem?.attribute_store?.[this.partNumberAttributeName];
            if (resolvedPartNumber && resolvedPartNumber !== searchedPartNumber) {
              this.mismatch = { searched: searchedPartNumber, resolved: resolvedPartNumber,
                                componentChanges, savedItem, from_batch: false };
              this.$bvModal.show('part-number-mismatch-modal');
            } else {
              this.$router.push({ name: 'quote_path' });
            }
          })
          .catch((error) => {
            alert(error.response ? error.response.data : error);
          });
    },
    saveOtherProduct(item){
      const searchedPartNumber = item.part_number;
      const componentChanges   = item.component_changes || [];
      this._saveProduct(item)
          .then((savedItem) => {
            const resolvedPartNumber = savedItem?.attribute_store?.[this.partNumberAttributeName];
            if (resolvedPartNumber && resolvedPartNumber !== searchedPartNumber) {
              this.mismatch = { searched: searchedPartNumber, resolved: resolvedPartNumber,
                                componentChanges, savedItem, from_batch: false };
              this.$bvModal.show('part-number-mismatch-modal');
            } else {
              this.$router.push({ name: 'quote_path' });
            }
          })
          .catch((error) => {
            alert(error.response ? error.response.data : error);
          });
    },
    async addSelectedToQuote() {
      this.saving = true;
      this.save_errors = [];
      this.mismatch_queue = [];
      const items = [...this.selected_items];
      this.save_progress = { completed: 0, total: items.length };
      const failed_items = [];

      for (const item of items) {
        try {
          const savedItem = await this._saveProduct(item);
          this.save_progress.completed += 1;
          const resolvedPartNumber = savedItem?.attribute_store?.[this.partNumberAttributeName];
          if (resolvedPartNumber && resolvedPartNumber !== item.part_number) {
            this.mismatch_queue.push({
              searched: item.part_number,
              resolved: resolvedPartNumber,
              componentChanges: item.component_changes || [],
              savedItem,
              from_batch: true
            });
          }
        } catch (err) {
          this.save_progress.completed += 1;
          this.save_errors.push({ part_number: item.part_number, error: err });
          failed_items.push(item);
        }
      }

      this.saving = false;
      this.selected_items = failed_items;

      if (this.mismatch_queue.length > 0) {
        this._showNextMismatch();
      } else if (this.save_errors.length === 0) {
        this.$router.push({ name: 'quote_path' });
      }
    },
    _showNextMismatch() {
      if (this.mismatch_queue.length > 0) {
        this.mismatch = this.mismatch_queue.shift();
        this.$bvModal.show('part-number-mismatch-modal');
        return;
      }
      if (this.save_errors.length === 0) {
        this.$router.push({ name: 'quote_path' });
      }
    },
    goToConfiguration() {
      this.$bvModal.hide('part-number-mismatch-modal');
      const user = this.$store.state.CurrentUserStore.user;
      this.$router.push({
        name: 'reconfigure_bto_path',
        params: {
          quote_version_id: this.$route.params.quote_version_id || user.current_quote_version_id,
          quote_part_id:    this.$route.params.quote_part_id    || user.current_quote_part_id,
          quote_product_id: this.mismatch.savedItem.id
        }
      });
    },
    addAnyway() {
      this.$bvModal.hide('part-number-mismatch-modal');
      if (this.mismatch?.from_batch) {
        this._showNextMismatch();
      } else {
        this.$router.push({ name: 'quote_path' });
      }
    }
  }
}
</script>

<style scoped>

</style>
